import { Platform } from 'react-native';
import {
  getCatalogRamSnapshotCache,
  type WebCatalogSupplierRow,
} from './catalogRamSnapshotCache';
import { buildAccentInsensitiveLikeClause, matchesSearchAny } from '../../shared/format/searchText';

export type SupplierRecord = {
  id: string;
  organizationId: string;
  name: string;
  code: string | null;
  taxId: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  paymentTerms: string | null;
  leadTimeDays: number | null;
  minimumOrderAmount: number | null;
  website: string | null;
  notes: string | null;
  accountNumber: string | null;
  isActive: boolean;
  deletedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type SupplierFilterState = 'all' | 'active' | 'inactive';

export const normalizeSupplierName = (value: string): string => value.trim();

export const mapLocalSupplierRow = (row: {
  id: string;
  organization_id: string;
  name: string;
  code: string | null;
  tax_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  payment_terms: string | null;
  lead_time_days: number | null;
  minimum_order_amount: number | null;
  website: string | null;
  notes: string | null;
  account_number: string | null;
  is_active: number | null;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}): SupplierRecord => ({
  id: row.id,
  organizationId: row.organization_id,
  name: row.name,
  code: row.code,
  taxId: row.tax_id,
  contactName: row.contact_name,
  contactEmail: row.contact_email,
  contactPhone: row.contact_phone,
  address: row.address,
  city: row.city,
  state: row.state,
  zip: row.zip,
  paymentTerms: row.payment_terms,
  leadTimeDays: row.lead_time_days,
  minimumOrderAmount: row.minimum_order_amount,
  website: row.website,
  notes: row.notes,
  accountNumber: row.account_number,
  isActive: row.is_active !== 0,
  deletedAt: row.deleted_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const mapWebSupplierRow = (row: WebCatalogSupplierRow): SupplierRecord => ({
  id: String(row.id),
  organizationId: String(row.organization_id ?? ''),
  name: String(row.name ?? '').trim(),
  code: row.code ?? null,
  taxId: row.tax_id ?? null,
  contactName: row.contact_name ?? null,
  contactEmail: row.contact_email ?? null,
  contactPhone: row.contact_phone ?? null,
  address: row.address ?? null,
  city: row.city ?? null,
  state: row.state ?? null,
  zip: row.zip ?? null,
  paymentTerms: row.payment_terms ?? null,
  leadTimeDays: row.lead_time_days ?? null,
  minimumOrderAmount: row.minimum_order_amount ?? null,
  website: row.website ?? null,
  notes: row.notes ?? null,
  accountNumber: row.account_number ?? null,
  isActive: row.is_active !== false,
  deletedAt: row.deleted_at ?? null,
  createdAt: row.created_at ?? null,
  updatedAt: row.updated_at ?? null,
});

export const filterWebSupplierRecords = (
  rows: WebCatalogSupplierRow[],
  organizationId: string,
  filterState: SupplierFilterState,
  search?: string
): SupplierRecord[] => {
  return rows
    .filter((row) => row?.id && String(row.organization_id ?? '') === organizationId)
    .filter((row) => !row.deleted_at)
    .filter((row) => {
      if (filterState === 'active') return row.is_active !== false;
      if (filterState === 'inactive') return row.is_active === false;
      return true;
    })
    .filter((row) =>
      matchesSearchAny([row.name, row.code, row.tax_id, row.contact_name, row.city], search ?? ''),
    )
    .map(mapWebSupplierRow)
    .sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
    });
};

export function suppliersFromWebCacheOrNull(
  organizationId: string,
  filterState: SupplierFilterState,
  search?: string,
  limit?: number,
  offset = 0
): SupplierRecord[] | null {
  if (Platform.OS !== 'web') return null;
  const cached = getCatalogRamSnapshotCache();
  if (!cached?.suppliers.length) return null;
  const filtered = filterWebSupplierRecords(cached.suppliers, organizationId, filterState, search);
  if (typeof limit === 'number') {
    const pageOffset = Math.max(0, offset);
    return filtered.slice(pageOffset, pageOffset + limit);
  }
  return filtered;
}

export const toSupplierSyncPayload = (supplier: SupplierRecord, organizationId: string) => ({
  organizationId,
  id: supplier.id,
  name: supplier.name,
  code: supplier.code,
  taxId: supplier.taxId,
  contactName: supplier.contactName,
  contactEmail: supplier.contactEmail,
  contactPhone: supplier.contactPhone,
  address: supplier.address,
  city: supplier.city,
  state: supplier.state,
  zip: supplier.zip,
  paymentTerms: supplier.paymentTerms,
  leadTimeDays: supplier.leadTimeDays,
  minimumOrderAmount: supplier.minimumOrderAmount,
  website: supplier.website,
  notes: supplier.notes,
  accountNumber: supplier.accountNumber,
  isActive: supplier.isActive,
  createdAt: supplier.createdAt ?? undefined,
  updatedAt: supplier.updatedAt ?? new Date().toISOString(),
  deletedAt: supplier.deletedAt,
});

/** Construye el fragmento de búsqueda (nombre, código, CUIT, contacto, ciudad) y sus parámetros. */
export const buildSupplierSearchClause = (search?: string): { clause: string; params: string[] } =>
  buildAccentInsensitiveLikeClause(
    ['name', "COALESCE(code, '')", "COALESCE(tax_id, '')", "COALESCE(contact_name, '')", "COALESCE(city, '')"],
    search,
  );

export const SUPPLIER_SELECT = `id, organization_id, name, code, tax_id, contact_name, contact_email, contact_phone,
            address, city, state, zip, payment_terms, lead_time_days, minimum_order_amount,
            website, notes, account_number, is_active, deleted_at, created_at, updated_at`;

